// Admin v2 — Ötletláda és kampány szavazás (Figma ADM_12, 12b–12e).
// Ötletek: user_ideas (a mobilapp sémája: title, description, category, status, upvotes, upvotedBy, userName).
// Sablonok: app_settings/ideabox; jelentések: reports (targetType: 'idea').
import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtInt, timeAgo, toDate } from '../data';
import {
  Badge, Button, Card, ConfirmModal, Empty, Field, Input, Modal, PageHeader, SectionTitle, Select, Spinner, StatCard, Tabs, TextArea, Toggle, Tone, useToast,
} from '../ui';

type Tab = 'ideas' | 'templates' | 'analytics' | 'reports';
type IStatus = 'pending' | 'review' | 'approved' | 'rejected';
interface Idea { id: string; title: string; description: string; category: string; status: IStatus; upvotes: number; userName: string; region: string; at: Date | null; note: string }
interface Tpl { id: string; name: string; type: string; fields: string[]; active: boolean }
interface Report { id: string; targetId: string; label: string; reason: string; reporter: string; at: Date | null }

const CATS: Record<string, string> = {
  celebrity: 'Javasolt híresség', challenge: 'Kihívás', charity: 'Jótékonysági akció', giveaway: 'Nyereményjáték',
  auction: 'Aukció', match: 'Mérkőzés', brand: 'Javasolt márka', other: 'Egyéb',
};
const CAT_SHORT: Record<string, string> = { auction: 'Auk.', challenge: 'Kih.', match: 'Mérk.', giveaway: 'Nyer.', charity: 'Jót.', celebrity: 'Hír.', brand: 'Márka', other: 'Egyéb' };
const ST: Record<IStatus, { t: string; tone: Tone }> = {
  pending: { t: 'Függőben', tone: 'gold' }, review: { t: 'Vizsgálat alatt', tone: 'cyan' }, approved: { t: 'Jóváhagyva', tone: 'green' }, rejected: { t: 'Elutasítva', tone: 'red' },
};
const TPL_REF = () => doc(db, 'app_settings', 'ideabox');

export default function IdeaBoxPage() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('ideas');
  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  const [tpls, setTpls] = useState<Tpl[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [q, setQ] = useState('');
  const [stF, setStF] = useState('all');
  const [catF, setCatF] = useState('all');
  const [mod, setMod] = useState<Idea | null>(null);
  const [del, setDel] = useState<Idea | null>(null);
  const [tplEdit, setTplEdit] = useState<Tpl | 'new' | null>(null);

  useEffect(() => onSnapshot(collection(db, 'user_ideas'), (s) => setIdeas(s.docs.map((d) => {
    const v = d.data();
    return {
      id: d.id, title: String(v.title ?? ''), description: String(v.description ?? ''), category: String(v.category ?? 'other'),
      status: (['pending', 'review', 'approved', 'rejected'].includes(v.status) ? v.status : 'pending') as IStatus,
      upvotes: Number(v.upvotes ?? 0), userName: String(v.userName ?? ''), region: String(v.region ?? v.city ?? ''), at: toDate(v.createdAt), note: String(v.moderatorNote ?? ''),
    };
  }).sort((a, b) => b.upvotes - a.upvotes)), () => setIdeas([])), []);
  useEffect(() => onSnapshot(TPL_REF(), (s) => setTpls(((s.data()?.templates as Tpl[]) ?? [])), () => undefined), []);
  useEffect(() => onSnapshot(query(collection(db, 'reports'), where('targetType', '==', 'idea'), limit(100)), (s) => setReports(s.docs.filter((d) => d.data().status !== 'reviewed').map((d) => {
    const v = d.data();
    return { id: d.id, targetId: String(v.targetId ?? ''), label: String(v.targetLabel ?? ''), reason: String(v.reason ?? ''), reporter: String(v.reporterName ?? v.reporterId ?? ''), at: toDate(v.createdAt) };
  })), () => setReports([])), []);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (ideas ?? []).filter((i) => (stF === 'all' || i.status === stF) && (catF === 'all' || i.category === catF)
      && (!t || [i.title, i.description, i.userName].some((f) => f.toLowerCase().includes(t))));
  }, [ideas, q, stF, catF]);

  const saveTpls = (l: Tpl[]) => setDoc(TPL_REF(), { templates: l }, { merge: true });

  return (
    <div>
      <PageHeader title="Ötletláda és kampány szavazás" sub="Közösségi ötletek moderálása és kampány sablonok kezelése" />
      <Tabs<Tab> value={tab} onChange={setTab} options={[{ value: 'ideas', label: 'Ötletek' }, { value: 'templates', label: 'Sablonok' }, { value: 'analytics', label: 'Analitika' }, { value: 'reports', label: 'Jelentések' }]} />

      {tab === 'ideas' && (
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <Input icon="MagnifyingGlass" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés cím, leírás vagy szerző alapján..." className="flex-1 min-w-[260px]" />
            <div className="w-[190px]"><Select value={stF} onChange={setStF} options={[{ value: 'all', label: 'Összes státusz' }, ...Object.entries(ST).map(([k, v]) => ({ value: k, label: v.t }))]} /></div>
            <div className="w-[190px]"><Select value={catF} onChange={setCatF} options={[{ value: 'all', label: 'Összes kategória' }, ...Object.entries(CATS).map(([k, v]) => ({ value: k, label: v }))]} /></div>
          </div>
          {ideas === null ? <Spinner /> : list.length === 0 ? <Card><Empty>Nincs a szűrésnek megfelelő ötlet.</Empty></Card> : (
            <div className="flex flex-col gap-3">
              {list.map((i) => (
                <Card key={i.id} className="!p-4 flex items-center gap-4 flex-wrap">
                  <div className="w-[64px] h-[64px] rounded-xl bg-[#1d232a] flex flex-col items-center justify-center shrink-0">
                    <Icon name="CaretUp" size={14} className="text-[#00AFF2]" />
                    <span className="text-[16px] font-bold text-white leading-5">{fmtInt(i.upvotes)}</span>
                    <span className="text-[10px] text-[#8a8f99]">szavazat</span>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-[14px] font-semibold text-white">{i.title}</div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <Badge tone="purple">{CATS[i.category] ?? 'Egyéb'}</Badge>
                      <span className="text-[12px] text-[#8a8f99]">Beküldte: {i.userName || '—'}{i.region ? ` · ${i.region}` : ''}</span>
                    </div>
                  </div>
                  <Badge tone={ST[i.status].tone}>{ST[i.status].t}</Badge>
                  <button type="button" onClick={() => setMod(i)} className="h-[32px] px-4 rounded-full border border-[#00AFF2] text-[#00AFF2] text-[13px] font-semibold hover:bg-[#00AFF2]/10">Moderálás</button>
                  <button type="button" onClick={() => setDel(i)} aria-label="Törlés" className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={16} /></button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'templates' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[15px] font-extrabold italic uppercase text-white">Sablon kezelés</h2>
            <Button icon="Plus" onClick={() => setTplEdit('new')}>Új sablon</Button>
          </div>
          {tpls.length === 0 ? <Card><Empty>Még nincs sablon.</Empty></Card> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {tpls.map((t) => (
                <Card key={t.id} className="!p-5">
                  <div className="flex items-center justify-between gap-2"><span className="text-[14px] font-semibold text-white">{t.name}</span><Badge tone={t.active ? 'green' : 'gray'}>{t.active ? 'Aktív' : 'Inaktív'}</Badge></div>
                  <div className="text-[12px] text-[#8a8f99] mt-1">Típus: {t.type}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">{t.fields.map((f) => <span key={f} className="h-[24px] px-2.5 inline-flex items-center rounded-full bg-[#31383D] text-[11px] text-[#B8B8C3]">{f}</span>)}</div>
                  <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px] mt-4" onClick={() => setTplEdit(t)}>Szerkesztés</Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'analytics' && <Analytics ideas={ideas ?? []} />}

      {tab === 'reports' && (
        <div>
          <SectionTitle right={<Badge tone="gold">{reports.length} nyitott</Badge>}>Jelentés kezelés</SectionTitle>
          {reports.length === 0 ? <Card><Empty>Nincs nyitott jelentés.</Empty></Card> : (
            <div className="flex flex-col gap-3">
              {reports.map((r) => {
                const idea = ideas?.find((i) => i.id === r.targetId);
                return (
                  <Card key={r.id} className="!p-4 flex items-center gap-4 flex-wrap">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#ef4444]/[0.14] text-[#ef4444]"><Icon name="Flag" size={18} /></span>
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-[14px] font-semibold text-white">{idea?.title || r.label || 'Ötlet'}</div>
                      <div className="text-[12px] text-[#8a8f99]">Ok: {r.reason || '—'} · {timeAgo(r.at)}</div>
                    </div>
                    <Button kind="ghost" icon="Check" onClick={async () => { await updateDoc(doc(db, 'reports', r.id), { status: 'reviewed', reviewedAt: serverTimestamp() }); toast('Jelentés felülvizsgálva.'); }}>Felülvizsgált</Button>
                    <button type="button" onClick={async () => {
                      if (idea) await updateDoc(doc(db, 'user_ideas', idea.id), { status: 'rejected', moderatedAt: serverTimestamp() });
                      await updateDoc(doc(db, 'reports', r.id), { status: 'reviewed', action: 'idea_rejected', reviewedAt: serverTimestamp() });
                      toast('Ötlet elutasítva.');
                    }} className="inline-flex items-center gap-1.5 h-[37px] px-4 rounded-full border border-[#ef4444] text-[#ef4444] text-[14px] font-semibold hover:bg-[#ef4444]/10"><Icon name="X" size={14} />Ötlet elutasítása</button>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {mod && <ModerateModal idea={mod} onClose={() => setMod(null)} />}
      {del && (
        <ConfirmModal title="Ötlet törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)} text={<>Biztosan törlöd: <b className="text-white">{del.title}</b>?</>}
          onConfirm={async () => { try { await deleteDoc(doc(db, 'user_ideas', del.id)); toast('Ötlet törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); } setDel(null); }} />
      )}
      {tplEdit && <TemplateModal t={tplEdit === 'new' ? null : tplEdit} onClose={() => setTplEdit(null)} onSave={async (t) => {
        await saveTpls(tplEdit === 'new' ? [...tpls, t] : tpls.map((x) => (x.id === t.id ? t : x)));
        toast('Sablon mentve.');
      }} onDelete={tplEdit !== 'new' ? async () => { await saveTpls(tpls.filter((x) => x.id !== (tplEdit as Tpl).id)); toast('Sablon törölve.'); } : undefined} />}
    </div>
  );
}

function ModerateModal({ idea, onClose }: { idea: Idea; onClose: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState(idea.note);
  const set = async (status: IStatus, msg: string) => {
    try {
      await updateDoc(doc(db, 'user_ideas', idea.id), { status, moderatorNote: note.trim() || null, moderatedAt: serverTimestamp() });
      toast(msg); onClose();
    } catch { toast('A mentés nem sikerült.', 'red'); }
  };
  return (
    <Modal title="Ötlet moderálása" onClose={onClose} width={560} footer={<>
      <button type="button" onClick={() => set('rejected', 'Ötlet elutasítva.')} className="h-[37px] px-4 rounded-full border border-[#ef4444] text-[#ef4444] text-[14px] font-semibold hover:bg-[#ef4444]/10">Elutasítás</button>
      <Button kind="ghost" onClick={() => set('review', 'Vizsgálat alá helyezve.')}>Vizsgálat</Button>
      <button type="button" onClick={() => set('approved', 'Ötlet jóváhagyva.')} className="inline-flex items-center gap-1.5 h-[37px] px-4 rounded-full bg-[#2bc48a] text-white text-[14px] font-semibold hover:brightness-110"><Icon name="Check" size={15} />Jóváhagyás</button>
    </>}>
      <div className="text-[16px] font-bold text-white">{idea.title}</div>
      <div className="flex items-center gap-2 mt-2 flex-wrap"><Badge tone="purple">{CATS[idea.category] ?? 'Egyéb'}</Badge><span className="text-[12px] text-[#8a8f99]">Beküldte: {idea.userName}{idea.region ? ` · Régió: ${idea.region}` : ''}</span></div>
      {idea.description && <p className="mt-3 text-[13px] text-[#B8B8C3] leading-relaxed">{idea.description}</p>}
      <div className="mt-4"><Field label="Moderátori megjegyzés"><TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Megjegyzés a beküldőnek..." /></Field></div>
    </Modal>
  );
}

function TemplateModal({ t, onClose, onSave, onDelete }: { t: Tpl | null; onClose: () => void; onSave: (t: Tpl) => Promise<void>; onDelete?: () => Promise<void> }) {
  const [f, setF] = useState<Tpl>(t ?? { id: `t_${Date.now()}`, name: '', type: 'Kihívás', fields: [], active: true });
  const [fields, setFields] = useState(f.fields.join(', '));
  return (
    <Modal title={t ? 'Sablon szerkesztése' : 'Új sablon'} onClose={onClose} width={480} footer={<>
      {onDelete && <button type="button" onClick={async () => { await onDelete(); onClose(); }} className="mr-auto text-[13px] font-semibold text-[#ef4444] hover:underline">Törlés</button>}
      <Button kind="ghost" onClick={onClose}>Mégse</Button>
      <Button disabled={!f.name.trim()} onClick={async () => { await onSave({ ...f, name: f.name.trim(), fields: fields.split(',').map((x) => x.trim()).filter(Boolean) }); onClose(); }}>Mentés</Button>
    </>}>
      <div className="flex flex-col gap-4">
        <Field label="Sablon neve"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="pl. Híresség kihívás" /></Field>
        <Field label="Típus"><Select value={f.type} onChange={(v) => setF({ ...f, type: v })} options={['Kihívás', 'Aukció', 'Nyereményjáték', 'Javasolt márka', 'Javasolt híresség', 'Jótékonysági akció'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Mezők (vesszővel elválasztva)"><Input value={fields} onChange={(e) => setFields(e.target.value)} placeholder="híresség, helyszín, dátum" /></Field>
        <div className="flex items-center justify-between px-4 h-[52px] rounded-xl bg-[#1d232a]"><span className="text-[14px] font-semibold text-white">Aktív</span><Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} /></div>
      </div>
    </Modal>
  );
}

function Analytics({ ideas }: { ideas: Idea[] }) {
  const votes = ideas.reduce((s, i) => s + i.upvotes, 0);
  const top = ideas.slice(0, 5);
  const maxV = Math.max(1, ...top.map((i) => i.upvotes));
  const byCat = Object.keys(CATS).map((k) => ({ k, n: ideas.filter((i) => i.category === k).length })).filter((x) => x.n > 0);
  const maxC = Math.max(1, ...byCat.map((x) => x.n));
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon="Lightning" tone="cyan" label="Összes ötlet" value={fmtInt(ideas.length)} />
        <StatCard icon="Clock" tone="gold" label="Függőben lévő" value={fmtInt(ideas.filter((i) => i.status === 'pending').length)} valueColor="#d9a514" />
        <StatCard icon="CheckCircle" tone="green" label="Összes szavazat" value={fmtInt(votes)} valueColor="#2bc48a" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-4 mt-6">
        <Card className="!p-5">
          <SectionTitle>Trendi ötletek</SectionTitle>
          {top.length === 0 ? <Empty>Még nincs ötlet.</Empty> : top.map((i) => (
            <div key={i.id} className="mb-3">
              <div className="flex justify-between text-[13px]"><span className="text-white truncate pr-3">{i.title}</span><span className="text-[#8a8f99] shrink-0">{fmtInt(i.upvotes)} szavazat</span></div>
              <div className="h-1.5 mt-1.5 rounded-full bg-[#31383D] overflow-hidden"><div className="h-full rounded-full bg-[#00AFF2]" style={{ width: `${(i.upvotes / maxV) * 100}%` }} /></div>
            </div>
          ))}
        </Card>
        <Card className="!p-5">
          <SectionTitle>Kategóriák szerint</SectionTitle>
          {byCat.length === 0 ? <Empty>Nincs adat.</Empty> : (
            <div className="flex items-end gap-3 h-[160px]">
              {byCat.map((x) => (
                <div key={x.k} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5" title={`${CATS[x.k]}: ${x.n}`}>
                  <div className="w-full rounded-t-md bg-gradient-to-b from-[#a855f7] to-[#6b2fb0]" style={{ height: `${Math.max(6, (x.n / maxC) * 130)}px` }} />
                  <span className="text-[10px] text-[#8a8f99]">{CAT_SHORT[x.k]}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
